import { ArrowLeftIcon, ChevronDownIcon } from "@heroicons/react/outline";
import PaystackPop from "@paystack/inline-js";
import Button from "components/lib/Shared/Button";
import { Constants } from "config/constants";
import { Form, Formik } from "formik";
import { useEffect, useRef, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  chargeSavedCardAsync,
  initializeTransactionAsync,
  verifyTransactionAsync,
} from "state/slices/paystack";
import { getCompletePackageName } from "utils";
import { BillingDetails } from "./BillingDetails";
import PaymentSummary from "./PaymentSummary";
import { validationSchema } from "./validationSchema";
import { getFullName } from "utils";
import _pick from "lodash/pick";
import { generateOrderIdAsync } from "state/slices/order";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useToast } from "hooks/useToast";
import { getCurrentUser } from "state/slices/auth";
import { usePopper } from "hooks/usePopper";
import { Menu, Transition } from "@headlessui/react";
import ConfirmDialog from "components/lib/Shared/ConfirmDialog";
import { closeModal, openModal } from "state/slices/modals";
import PaymentConfirmationModal from "./PaymentConfirmationModal";

const PaystackPaymentMethod = ({
  companyType,
  selectedPackage,
  discountPercentage,
  selectedCurrency,
  handleGoBack,
  abbrevCompanyType,
}) => {
  const paystackRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [accessCode, setAccessCode] = useState(null);
  const [invoice, setInvoice] = useState({});
  const dispatch = useDispatch();
  const history = useHistory();
  const { toast } = useToast();
  const [trigger, container] = usePopper({
    placement: "bottom-end",
    strategy: "fixed",
    modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
  });

  const userSlice = useSelector((state) => state.userSlice);
  const sflData = Constants.SFL_DATA;
  const savedCards = userSlice?.savedCards || [];
  let latestCard = null;
  if (savedCards.length > 0) {
    latestCard = savedCards[savedCards.length - 1];
  }
  let orgAdminProfitMargin = null;
  if (userSlice?.orgAdmin) {
    orgAdminProfitMargin = selectedCurrency?.orgAdminProfitMargins?.find(
      (profitmargin) =>
        profitmargin?.orgAdmin?.toString() === userSlice.orgAdmin?.toString()
    )?.profitMargin;
  }
  const actualAmount =
    (selectedCurrency.basePrice +
      (orgAdminProfitMargin
        ? orgAdminProfitMargin
        : selectedCurrency?.profitMargin ?? 0)) *
    selectedCurrency.applicableTax;
  const discountAmount = discountPercentage
    ? actualAmount * (discountPercentage / 100)
    : 0;
  const totalAmount = actualAmount - discountAmount;
  const paystackAmount = totalAmount * 100;

  const initialValues = {
    firstName: userSlice?.names?.firstName ?? "",
    lastName: userSlice?.names?.lastName ?? "",
    email: userSlice?.email ?? "",
    mobileNumber: userSlice?.electronicAddress?.mobileNumber ?? "",
    billingAddress: [
      "Kenyan Citizen",
      "Foreign Resident",
      "Local Company",
    ].includes(userSlice.idType)
      ? `PO BOX ${userSlice?.postalAddress?.postalAddress} ${userSlice?.postalAddress?.postalCode}`
      : userSlice?.postalAddress?.postalAddress,
    paymentMethod: "mpesa",
    mpesaPhoneNumber:
      userSlice?.mpesaPhoneNumber ??
      userSlice?.electronicAddress?.mobileNumber?.replace("+", "") ??
      "",
    cardHolderName: "",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
  };

  const generateInvoice = (values) => {
    const { paymentMethod } = values;
    const invoice = {
      plan: selectedPackage.name,
      billFrom: {
        name: sflData.name,
        address: `${sflData.buildingName}, ${sflData.floorNumber}, ${sflData.streetName}, ${sflData.location.county}`,
        pin: sflData.pin,
      },
      billTo: {
        name: getFullName(userSlice.names),
        address: values.billingAddress,
        pin: userSlice.identificationDetails?.KRAPin,
      },
      items: [
        {
          description: selectedPackage.product.description,
          quantity: 1,
          actualAmount,
          discountAmount,
          rate: totalAmount,
        },
      ],
      currency: selectedCurrency.type,
      isTaxApplicable: selectedCurrency.applicableTax > 1,
      applicableTax: selectedCurrency.applicableTax,
      paymentMethod,
      ..._pick(values, ["firstName", "email"]),
    };

    return invoice;
  };

  const verifyTransaction = async (reference, invoice) => {
    const generateOrderIdResponse = await dispatch(
      generateOrderIdAsync({ type: abbrevCompanyType })
    );

    const orderId = generateOrderIdResponse.payload.orderId;
    const payload = {
      company: { type: abbrevCompanyType },
      orderId,
      invoice,
      user: userSlice,
    };

    // Verify on your backend before you “confirm” payment
    const res = await dispatch(
      verifyTransactionAsync({ reference, data: payload })
    );

    if (res.payload.data) {
      await dispatch(getCurrentUser());
      const { redirectUrl } = res.payload.data;
      if (redirectUrl) {
        setLoading(false);
        history.push(redirectUrl);
      }
    }
  };

  const initializePaystackPopup = () => {
    // Initiate paystack popup
    const popup = new PaystackPop();
    popup.resumeTransaction(accessCode, {
      onSuccess: async (tx) => {
        // tx.reference is what you verify server-side
        const ref = tx.reference;
        await verifyTransaction(ref, invoice);
      },

      onCancel: () => {
        setLoading(false);
      },

      onError: (err) => {
        setLoading(false);
        history.push("/payment-failed");
      },

      metadata: {
        custom_fields: [
          {
            display_name: "Phone Number",
            variable_name: "phone_number",
            value: "0768583817",
          },
        ],
      },
    });
  };

  useEffect(() => {
    if (accessCode) {
      initializePaystackPopup();
    }
  }, [accessCode]);

  const handleMakePayment = async (values) => {
    try {
      setLoading(true);
      setInvoice(generateInvoice(values));
      const response = await dispatch(
        initializeTransactionAsync({
          data: {
            email: values?.email,
            amount: paystackAmount, // paystack expects the least denomination i.e amount in cents,
          },
        })
      );
      if (response.payload?.data?.access_code) {
        setAccessCode(response.payload.data?.access_code);
      } else {
        toast(
          "error",
          response.payload?.messageText ?? "Could not initialize transaction"
        );
        setLoading(false);
      }
      return;
    } catch (err) {
      toast("error", "Something went wrong, please try again.");
    }
  };

  const handlePayWithSavedCard = async (values) => {
    setLoading(true);
    const invoice = generateInvoice(values);
    const chargeResponse = await dispatch(
      chargeSavedCardAsync({
        data: {
          email: latestCard.customer.email,
          authorization_code: latestCard.authorization.authorization_code,
          amount: paystackAmount > 300 ? paystackAmount : 300,
        },
      })
    );
    if (chargeResponse.payload?.data?.status === "success") {
      await verifyTransaction(chargeResponse.payload.data.reference, invoice);
    } else {
      toast(
        "error",
        chargeResponse.payload?.data?.gateway_response ||
          "Failed to charge the saved card"
      );
    }
    setLoading(false);
    dispatch(closeModal("payment_confirmation_modal"));
  };

  const openPaymentConfirmationModal = () => {
    setLoading(true);
    dispatch(
      openModal({
        modalName: "payment_confirmation_modal",
        modalRow: null,
      })
    );
  };

  const closePaymentConfirmationModal = (values) => {
    dispatch(closeModal("payment_confirmation_modal"));
    handleMakePayment(values);
  };

  const PaymentOptionsDropdown = ({ values }) => {
    return (
      <div className="flex justify-between">
        <Button
          className="py-2 rounded-r-none px-4 text-white border border-transparent shadow-sm bg-sflPrimary hover:bg-green-primary hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sflPrimary focus:bg-green-focused disabled:bg-optional"
          isLoading={loading}
          loadingText="Processing..."
          onClick={() => handlePayWithSavedCard(values)}
        >
          Process payment
        </Button>
        <Menu as="div" className="relative -ml-px block">
          <Menu.Button
            ref={trigger}
            disabled={loading}
            className="relative inline-flex border-l border-white items-center rounded-r-md bg-sflPrimary px-2 py-2 text-white hover:bg-green-700 focus:z-10 disabled:cursor-not-allowed disabled:bg-green-200"
          >
            <span className="sr-only">Open options</span>
            <ChevronDownIcon className="h-6 w-6" aria-hidden="true" />
          </Menu.Button>
          <div className="z-50 mt-0 py-0">
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 -mr-1 mt-0 w-max origin-top-right rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-0">
                  <Button
                    className="py-2 px-4 text-white hover:text-white border border-transparent shadow-sm bg-green-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sflPrimary focus:bg-green-focused disabled:bg-optional"
                    onClick={() => handleMakePayment(values)}
                    isLoading={loading}
                    loadingText="Processing..."
                  >
                    Add payment method
                  </Button>
                </div>
              </Menu.Items>
            </Transition>
          </div>
        </Menu>
      </div>
    );
  };

  return (
    <div>
      <Button
        onClick={() => handleGoBack(0)}
        variant="text"
        className="flex space-x-2 text-sm font-medium rounded-md group text-gray-600 mb-2"
      >
        <ArrowLeftIcon
          className="w-5 h-5 text-gray-400 group-hover:text-gray-800"
          aria-hidden="true"
        />
        <span className="group-hover:text-gray-800">Back</span>
      </Button>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({ values, errors }) => {
          return (
            <div>
              <Form>
                <div className="grid  lg:grid-cols-5 gap-4 Wrapper text-primary">
                  <div className="col-span-3">
                    <div className="space-y-1 text-sm">
                      <h3>
                        You have selected to set up a <b>{companyType}</b> with
                        the <b>{getCompletePackageName(selectedPackage)}</b>{" "}
                        package.
                      </h3>
                    </div>
                    <div>
                      <div className="py-3">
                        <h1 className="font-medium">
                          Step: 1 of 2: Billing details
                        </h1>
                        <hr className="my-1" />
                      </div>
                      <BillingDetails />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <PaymentSummary
                      selectedCurrencyType={selectedCurrency.type}
                      actualAmount={actualAmount}
                      discountAmount={discountAmount}
                    />
                    <div className="my-4 text-sm float-right">
                      {savedCards.length > 0 ? (
                        <PaymentOptionsDropdown values={values} />
                      ) : (
                        <Button
                          disabled={loading}
                          type="submit"
                          onClick={() => handleMakePayment(values)}
                        >
                          {loading ? "Processing..." : "Process Payment"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Form>
              <ConfirmDialog
                name="payment_confirmation_modal"
                onClose={() => closePaymentConfirmationModal(values)}
                onConfirm={() => handlePayWithSavedCard(values)}
                title=""
                loadingText="Processing"
                callHandleClose={false}
              >
                {latestCard && (
                  <PaymentConfirmationModal
                    authorization={latestCard.authorization}
                    customer={latestCard.customer}
                    amountLabel={totalAmount}
                    selectedCurrencyType={selectedCurrency.type}
                    merchantName="SFL Workspace"
                  />
                )}
              </ConfirmDialog>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};

export default PaystackPaymentMethod;
